import { FC, useEffect, useState, Component, ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { fetchData, toasterrormsg, useMenuIdPermissions } from '../../../../reusable'
import { KTIcon, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import Loader from '../../components/Loader'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { PropertySelectionStep } from './PropertySelectionStep'
import { TargetingStep } from './TargetingStep'
import { BudgetPaymentStep } from './BudgetPaymentStep'
import { SponserData, SelectOption, SponserStepCommonProps, SponserTargetMasterOption } from '../../types/sponser'

// ── Error Boundary (class required by React — no hook equivalent) ──
class StepErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(error: Error) { console.error('Step render error:', error) }
  render() {
    if (this.state.hasError) {
      return (
        <div className='text-center py-10'>
          <p className='text-muted fs-6'>This section encountered an error.</p>
          <button className='btn btn-light-primary btn-sm mt-3' onClick={() => this.setState({ hasError: false })}>
            Retry
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ── Constants ──
const STEPS = [
  { key: 'action', label: 'Property Selection', icon: 'geolocation-home', completeKey: 'action' },
  { key: 'targeting', label: 'Targeting', icon: 'focus', completeKey: 'target' },
  { key: 'budget', label: 'Budget & Payment', icon: 'dollar', completeKey: 'payment' },
] as const


// ── Component ──
const getInitialStep = (searchParams: URLSearchParams): number => {
  const stepParam = searchParams.get('step')
  if (!stepParam) return 0
  const idx = STEPS.findIndex((s) => s.key === stepParam)
  return idx >= 0 ? idx : 0
}

const AddEditSearchAd: FC = () => {
  const t = Tr()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { urlData } = useMenuIdPermissions() as any
  const sponserId = Number(urlData?.sponserId) || 0
  const isEdit = sponserId > 0

  const [activeStep, setActiveStep] = useState(() => getInitialStep(searchParams))
  const [isLoading, setIsLoading] = useState(false)
  const [pageError, setPageError] = useState('')
  const [retrying, setRetrying] = useState(false)

  // Core data
  const [sponserData, setSponserData] = useState<SponserData | null>(null)
  const [targetMasters, setTargetMasters] = useState<SponserTargetMasterOption[]>([])
  const [vendorList, setVendorList] = useState<SelectOption[]>([])
  const [regionList, setRegionList] = useState<SelectOption[]>([])
  const [interestList, setInterestList] = useState<SelectOption[]>([])
  const [imageBaseUrl, setImageBaseUrl] = useState('')
  const [currentSponserId, setCurrentSponserId] = useState(sponserId)

  const tmp = sessionStorage.getItem('admin')
  const adminId = tmp ? JSON.parse(tmp)?.adminId ?? '' : ''

  // ── Fetch Master Data (sub-APIs — never block the page, keep defaults on failure) ──
  const fetchMasterData = async () => {
    const [targetRes, vendorRes, regionRes, interestRes] = await Promise.all([
      fetchData('admin/sponser/targetmaster/list', {}).catch(() => null),
      fetchData('admin/vendorList', {}).catch(() => null),
      fetchData('vendor/public/regionList', []).catch(() => null),
      fetchData('admin/interestlist', {}).catch(() => null),
    ])

    if (targetRes?.status == '200') {
      const list = targetRes?.data?.list ?? targetRes?.data ?? []
      const active = (Array.isArray(list) ? list : []).filter((i: any) => i?.isActive)
      setTargetMasters(active)
    }
    if (vendorRes?.status == '200') {
      const list = Array.isArray(vendorRes?.data) ? vendorRes.data : []
      setVendorList(list.map((v: any) => ({ value: v?.vendorId ?? '', label: v?.fullName ?? '-' })))
    }
    if (regionRes?.status == '200') {
      const list = Array.isArray(regionRes?.data) ? regionRes.data : []
      setRegionList(list.map((r: any) => ({ value: r?.regionId ?? '', label: r?.en_name ?? r?._name ?? '-' })))
    }
    if (interestRes?.status == '200') {
      const list = Array.isArray(interestRes?.data) ? interestRes.data : []
      setInterestList(list.map((i: any) => ({ value: i?.interestId ?? '', label: i?.en_name ?? '-' })))
    }
  }

  // ── Fetch Sponser Detail (main API — controls error screen) ──
  const fetchSponserDetail = async (): Promise<boolean> => {
    if (!sponserId) return true
    try {
      const detailResponse = await fetchData('admin/sponser/detail', { sponserId })
      if (detailResponse?.status == '200' && detailResponse?.data) {
        setSponserData(detailResponse.data)
        if (detailResponse?.url) setImageBaseUrl(detailResponse.url)
        return true
      }
      return false
    } catch (error) {
      console.error('Error fetching sponser detail:', error)
      return false
    }
  }

  // ── Load all data (initial + retry) ──
  const loadAllData = async ({ showLoader = true, isRetry = false } = {}) => {
    if (showLoader) setIsLoading(true)
    if (isRetry) setRetrying(true)

    try {
      const [, detailOk] = await Promise.all([fetchMasterData(), fetchSponserDetail()])
      if (detailOk) {
        setPageError('')
      } else {
        if (isRetry) {
          toasterrormsg(t('Failed to load data. Please try again.'))
        } else {
          setPageError('Error fetching data')
        }
      }
    } catch {
      if (isRetry) {
        toasterrormsg(t('Failed to load data. Please try again.'))
      } else {
        setPageError('Error fetching data')
      }
    } finally {
      setIsLoading(false)
      setRetrying(false)
    }
  }

  // Sync step to URL + meta title
  useEffect(() => {
    const stepKey = STEPS[activeStep]?.key
    if (stepKey) {
      const next = new URLSearchParams(searchParams)
      next.set('step', stepKey)
      setSearchParams(next, { replace: true })
    }
    document.title = `Insayr | ${isEdit ? 'Edit' : 'Add'} Search Advertisement`
  }, [activeStep])

  useEffect(() => {
    loadAllData()
  }, [sponserId])

  // Called by each step after successful save
  const handleStepSaveSuccess = (data?: any) => {
    if (data?.sponserId) setCurrentSponserId(data.sponserId)

    const refreshId = data?.sponserId || currentSponserId
    if (refreshId) {
      const refreshParam = { sponserId: refreshId }
      fetchData('admin/sponser/detail', refreshParam).then((detailResponse) => {
        if (detailResponse?.status == '200' && detailResponse?.data) {
          setSponserData(detailResponse.data)
          if (detailResponse?.url) setImageBaseUrl(detailResponse.url)
        }
      }).catch(() => {})
    }
  }

  // ── Navigation ──
  const goToNext = () => {
    if (activeStep < STEPS.length - 1) setActiveStep(activeStep + 1)
  }
  const goToPrevious = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1)
  }
  const goToStep = (idx: number) => {
    if (idx < 0 || idx >= STEPS.length) return
    const completeSteps = sponserData?.completeSteps ?? {}
    // Allow navigating to any completed step or the first incomplete step
    const allPreviousDone = STEPS.slice(0, idx).every((s) => !!completeSteps[s.completeKey])
    const targetStepDone = !!completeSteps[STEPS[idx].completeKey]
    if (targetStepDone || allPreviousDone || idx <= activeStep) {
      setActiveStep(idx)
    }
  }

  const effectiveSponserId = currentSponserId || sponserId
  const effectiveVendorId = sponserData?.vendorId ?? 0

  const commonProps: SponserStepCommonProps = {
    t,
    sponserId: effectiveSponserId,
    adminId: String(adminId),
    vendorId: effectiveVendorId,
    sponserData: sponserData,
    imageBaseUrl,
    onSaveSuccess: handleStepSaveSuccess,
  }

  // ── Render Step ──
  const renderStep = () => {
    switch (STEPS[activeStep]?.key) {
      case 'action':
        return <PropertySelectionStep {...commonProps} vendorList={vendorList} goToNext={goToNext} />
      case 'targeting':
        return <TargetingStep {...commonProps} targetMasters={targetMasters} regionList={regionList} interestList={interestList} goToNext={goToNext} />
      case 'budget':
        return <BudgetPaymentStep {...commonProps} />
      default:
        return <div className='text-muted text-center py-10'>{t('Unknown step')}</div>
    }
  }

  if (pageError) {
    return (
      <>
        <ToastContainer />
        <div className='d-flex flex-column align-items-center justify-content-center py-15 px-5'>
          <img
            src={toAbsoluteUrl('/media/illustrations/sketchy-1/21.png')}
            alt='Error illustration'
            className='mw-100 mb-8'
            style={{ maxHeight: '220px', objectFit: 'contain' as const }}
          />
          <h3 className='fw-bold text-gray-800 mb-2'>{t('Error')}</h3>
          <p className='text-gray-500 fs-6 text-center mb-8' style={{ maxWidth: '400px' }}>
            {pageError}
          </p>
          <div className='d-flex gap-3'>
            <button
              className='btn btn-primary d-flex align-items-center gap-2 px-6'
              disabled={retrying}
              onClick={() => loadAllData({ showLoader: false, isRetry: true })}
            >
              {retrying ? (
                <span className='spinner-border spinner-border-sm' role='status' />
              ) : (
                <KTIcon iconName='arrows-circle' iconType='solid' className='fs-3' />
              )}
              {t('Retry')}
            </button>
            <button
              className='btn btn-secondary d-flex align-items-center gap-2 px-6'
              onClick={() => navigate(-1)}
            >
              <KTIcon iconName='arrow-left' className='fs-3' />
              {t('Go Back')}
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}

      {/* Header */}
      <div className='d-flex flex-wrap align-items-center gap-3 mb-5'>
        <button className='btn btn-light-danger btn-sm d-flex align-items-center gap-2' onClick={() => navigate(-1)}>
          <KTIcon iconName='arrow-left' className='fs-3' />
          {t('Back')}
        </button>
        <h3 className='fw-bold mb-0 fs-3'>{isEdit ? t('Edit Sponser') : t('Add Sponser')}</h3>
      </div>

      <div className='sponserform-steps-wrapper'>
        <div className='sponserform-steps'>
          {STEPS.map((step, idx) => {
            const completeSteps = sponserData?.completeSteps ?? {}
            const isDone = !!completeSteps[step.completeKey]
            const isActive = idx === activeStep
            const isDisabled = idx > 0 && effectiveSponserId === 0
            return (
              <div className='sponserform-step-item' key={step.key}>
                {idx > 0 && (
                  <div className={`sponserform-step-connector ${isDone ? 'sponserform-step-connector--done' : isActive ? 'sponserform-step-connector--active' : ''}`} />
                )}
                <button
                  type='button'
                  className={`sponserform-step ${isActive && isDone ? 'sponserform-step--active-done' : isActive ? 'sponserform-step--active' : isDone ? 'sponserform-step--done' : ''}`}
                  onClick={() => goToStep(idx)}
                  disabled={isDisabled}
                >
                  <div className='sponserform-step__circle' />
                  <span className='sponserform-step__label'>{t(step.label)}</span>
                </button>
              </div>
            )
          })}
        </div>
      </div>

      <div className='card'>
        <div className='card-body p-5 p-md-8'>
          <StepErrorBoundary>
            {renderStep()}
          </StepErrorBoundary>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className='sponserform-footer'>
        {activeStep > 0 && (
          <button type='button' className='btn btn-secondary d-flex align-items-center gap-2 px-5 py-3' onClick={goToPrevious}>
            <KTIcon iconName='arrow-left' className='fs-3' />
            <span>{t('Previous')}</span>
          </button>
        )}
      </div>
    </>
  )
}

export { AddEditSearchAd }
